/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { selectEditMode } from "../../slices/editMode/editModeSlice";
import { resourceUpdated, selectAllResources, selectResourceById } from "../../slices/resourceCollection/resourceCollectionSlice";
import { selectAllResourceChanges } from "../../slices/resourceChangeReport/resourceChangeReportSlice";
import { useEffect, useState } from "react";
import { Button, Table } from "semantic-ui-react";
import getIdFromReference from "../../functions/getIdFromReference";
import { emptyReasonNotStarted, sectionCodeResults } from "../../dictionaries/CodeSystemLookup";
import updateChangeReport from "../../slices/resourceChangeReport/updateChangeReport";
import { simpleDisplayStringFromFHIRCodeableConcept } from "../../datatypeComponents/CodeableConceptEntry";
import AddTableRowModal from "./AddTableRowModal";
import EditTableCellEvidenceOnlyModal from "./EditTableCellEvidenceOnlyModal";
import { DisplayFromFHIRReference } from "../../datatypeComponents/ReferenceEntry";
import { changeEvidenceTableMap, selectEvidenceTableMap } from "../../slices/evidenceTableMap/evidenceTableMapSlice";

const autoEditEmptyReason = (newSection) => {
    if (newSection) {
        if (newSection.section?.length || newSection.entry?.length ||
            (newSection.text && newSection.text.status !== "empty")) {
            if (newSection.emptyReason) {
                delete newSection.emptyReason;
            }
        } else if (!newSection.emptyReason) {
            newSection.emptyReason = emptyReasonNotStarted;
        }
    }
    return newSection;
};

const calculateStartingSection = (startingValue) => {
    let startingSection = {
        extension: [],
        title: "",
        code: sectionCodeResults,
        focus: "", text: "", orderedBy: "", entry: [], emptyReason: "", section: []
    }
    if (startingValue) {
        if (startingValue.extension) { startingSection.extension = startingValue.extension; }
        else { delete startingSection.extension; }
        if (startingValue.title) { startingSection.title = startingValue.title; }
        else { delete startingSection.title; }
        if (startingValue.code) { startingSection.code = startingValue.code; }
        if (startingValue.author) { startingSection.author = startingValue.author; }
        else { delete startingSection.author; }
        if (startingValue.focus) { startingSection.focus = startingValue.focus; }
        else { delete startingSection.focus; }
        if (startingValue.text) { startingSection.text = startingValue.text; }
        else { delete startingSection.text; }
        if (startingValue.orderedBy) { startingSection.orderedBy = startingValue.orderedBy; }
        else { delete startingSection.orderedBy; }
        if (startingValue.entry) { startingSection.entry = startingValue.entry; }
        if (startingValue.emptyReason) { startingSection.emptyReason = startingValue.emptyReason; }
        else { delete startingSection.emptyReason; }
        if (startingValue.section) { startingSection.section = startingValue.section; }
        else { delete startingSection.section; }
    }
    return startingSection;
};

const EvidenceTableCellDataEntry = ({ startingValue, populationGroup, synthesisPopulationGroup,
    outcomeName, outcomeFocus, outcomeGroup, setEntryArrayState,
    statisticType, statisticUnit, numberAnalyzed,
    setReferencedEvidenceRevisedState, contained }) => {

    const evidenceTableMap = useSelector(selectEvidenceTableMap);
    const editMode = useSelector(selectEditMode);

    let compositionId = evidenceTableMap.compositionId;
    let compositionTitle = evidenceTableMap.compositionTitle;
    let combinedDictionary = evidenceTableMap.combinedDictionary;
    let evidenceDisplayDictionary = evidenceTableMap.evidenceDisplayDictionary;
    let suggestedEvidenceTitle = "Evidence for " + outcomeName + " in " + outcomeGroup.display;
    if (synthesisPopulationGroup?.display) {
        suggestedEvidenceTitle += " in " + synthesisPopulationGroup.display;
    } else if (populationGroup?.display) {
        suggestedEvidenceTitle += " in " + populationGroup.display;
    }
    let partOfReference = {
        "reference": "Composition/" + compositionId,
        "type": "Composition",
        "display": compositionTitle
    }
    let variableDefinition = [{
        "variableRole": "population",
        "observed": outcomeGroup
    }, {
        "variableRole": "outcome",
        "observed": outcomeFocus
    }];
    if (outcomeGroup.type === "EvidenceVariable") {
        variableDefinition = [{
            "variableRole": "population",
            "observed": populationGroup
        },
        {
            "variableRole": "exposure",
            "observed": outcomeGroup
        },
        {
            "variableRole": "outcome",
            "observed": outcomeFocus
        }];
        if (synthesisPopulationGroup) {
            variableDefinition[0]["intended"] = synthesisPopulationGroup;
            delete variableDefinition[0]["observed"];
        }
    }
    let entryAddElementValues = {
        title: combinedDictionary?.[startingValue?.entry?.[0]?.reference?.split("/")[1]]?.title || suggestedEvidenceTitle,
        relatesTo: [{
            "type": {
                "coding": [
                    {
                        "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                        "code": "part-of",
                        "display": "Part Of"
                    }
                ]
            },
            "targetReference": partOfReference
        }],
        description: combinedDictionary?.[startingValue?.entry?.[0]?.reference?.split("/")[1]]?.description || "",
        variableDefinition: variableDefinition,
        statistic: combinedDictionary?.[startingValue?.entry?.[0]?.reference?.split("/")[1]]?.statistic
    };
    if (contained) {
        entryAddElementValues.contained = contained;
    }

    const [openEditModalState, setOpenEditModalState] = useState({ "openModal": false });
    const [entryInstanceState, setEntryInstanceState] = useState({});
    const [evidenceDisplayState, setEvidenceDisplayState] = useState("");

    useEffect(() => {
        let startingEntryInstance = {
            reference: "", type: "Evidence", identifier: "", display: ""
        }
        if (startingValue) {
            if (startingValue.extension) { startingEntryInstance.extension = startingValue.extension; }
            if (startingValue.reference) { startingEntryInstance.reference = startingValue.reference; }
            if (startingValue.type) { startingEntryInstance.type = startingValue.type; }
            if (startingValue.identifier) { startingEntryInstance.identifier = startingValue.identifier; }
            if (startingValue.display) { startingEntryInstance.display = startingValue.display; }
        }
        setEntryInstanceState(JSON.parse(JSON.stringify(startingEntryInstance)));
    }, []);

    useEffect(() => {
        if (getIdFromReference(entryInstanceState) &&
            evidenceDisplayDictionary[getIdFromReference(entryInstanceState)]
        ) {
            setEvidenceDisplayState(evidenceDisplayDictionary[getIdFromReference(entryInstanceState)]);
        }
    }, [evidenceDisplayDictionary, entryInstanceState]);

    useEffect((() => {
        if (Object.keys(entryInstanceState).length > 0) {
            let newReference = {};
            if (entryInstanceState.extension) { newReference.extension = entryInstanceState.extension; }
            if (entryInstanceState.reference) { newReference.reference = entryInstanceState.reference; }
            if (entryInstanceState.type) { newReference.type = entryInstanceState.type; }
            if (entryInstanceState.identifier) { newReference.identifier = entryInstanceState.identifier; }
            if (entryInstanceState.display) { newReference.display = entryInstanceState.display; }
            if (Object.keys(newReference).length === 0 ||
                (Object.keys(newReference).length === 1 && newReference.type === "Evidence")) {
                newReference = null;
            }
            setEntryArrayState(prevState => {
                return { ...prevState, [getIdFromReference(outcomeGroup)]: newReference }
            });
        }
    }), [entryInstanceState]);

    if (openEditModalState?.openModal) {
        return <EditTableCellEvidenceOnlyModal
            entryInstance={entryInstanceState} setEntryInstanceState={setEntryInstanceState}
            addElementValues={entryAddElementValues}
            setOpenEditModalState={setOpenEditModalState}
            statisticType={statisticType} statisticUnit={statisticUnit}
            numberAnalyzed={numberAnalyzed}
            setReferencedEvidenceRevisedState={setReferencedEvidenceRevisedState} />
    } else {
        return <div>
            {startingValue ?
                <>
                    <div style={{ marginLeft: "24px" }}>
                        {evidenceDisplayState ?
                            <div>{evidenceDisplayState}
                            </div>
                            :
                            <p>[No data.]</p>
                        }
                        <br />
                        <DisplayFromFHIRReference reference={startingValue} />
                        <br />
                    </div>
                </>
                :
                <p>[No data.]</p>
            }
            {editMode &&
                <>
                    &nbsp;&nbsp;
                    <span className={"unselectable"} style={{ cursor: "pointer" }}
                        onClick={() => {
                            setOpenEditModalState({ openModal: true });
                        }}>
                        {startingValue ? <>✎ Edit</> : <>➕ Add</>}
                    </span>
                </>}
        </div>
    }

};

const TableRowSectionDataEntry = ({ startingValue, setTableRowSectionState, sampleSizeDefaultState,
    populationGroup, synthesisPopulationGroup }) => {

    const editMode = useSelector(selectEditMode);
    const evidenceTableMap = useSelector(selectEvidenceTableMap);

    let outcomeName = startingValue?.title || startingValue?.focus?.display;
    let groupReferencesDictionary = evidenceTableMap.groupReferencesDictionary;
    let combinedDictionary = evidenceTableMap.combinedDictionary;
    let groupKeys = Object.keys(groupReferencesDictionary);

    const [entryArrayState, setEntryArrayState] = useState(null);
    const [startingEntryArrayState, setStartingEntryArrayState] = useState(null);
    const [statisticDefaultsState, setStatisticDefaultsState] = useState({
        "statisticType": "", "units": ""
    });
    const [selectedStatisticDefaultsState, setSelectedStatisticDefaultsState] = useState({
        "statisticType": "", "units": ""
    });
    const [referencedEvidenceRevisedState, setReferencedEvidenceRevisedState] = useState({});

    useEffect(() => {
        let startingEntryStateValues = {};
        let defaultStatisticType = "";
        let defaultStatisticUnits = "";
        if (startingValue?.entry?.length > 0) {
            for (let itemIndex in startingValue.entry) {
                let evidenceReference = startingValue.entry[itemIndex];
                let evidenceId = getIdFromReference(evidenceReference);
                let item = combinedDictionary[evidenceId];
                let entryJson;
                if (item?.resourceType === "Evidence") {
                    if (item.variableDefinition?.[1]?.variableRole === "exposure") {
                        entryJson = item.variableDefinition[1].observed || item.variableDefinition[1].intended;
                    } else if (item.variableDefinition?.[0]?.variableRole === "population") {
                        entryJson = item.variableDefinition[0].observed || item.variableDefinition[0].intended;
                    }
                }
                if (entryJson) {
                    let entryId = getIdFromReference(entryJson);
                    startingEntryStateValues[entryId] = evidenceReference;
                }
                if (!defaultStatisticType && combinedDictionary &&
                    combinedDictionary[evidenceId]) {
                    let evidenceJson = combinedDictionary[evidenceId];
                    if (evidenceJson.statistic?.[0]?.statisticType) {
                        defaultStatisticType = evidenceJson.statistic[0].statisticType;
                        if (!defaultStatisticUnits && evidenceJson.statistic?.[0]?.quantity?.unit) {
                            defaultStatisticUnits = evidenceJson.statistic[0].quantity.unit;
                        }
                    }
                }
            }
        }
        setEntryArrayState(JSON.parse(JSON.stringify(startingEntryStateValues)));
        setStartingEntryArrayState(JSON.parse(JSON.stringify(startingEntryStateValues)));
        setStatisticDefaultsState({
            "statisticType": defaultStatisticType, "units": defaultStatisticUnits
        });
    }, []);

    useEffect(() => {
        if (entryArrayState &&
            JSON.stringify(entryArrayState) !== JSON.stringify(startingEntryArrayState)
        ) {
            let newEntryArray = [];
            for (const key in entryArrayState) {
                if (entryArrayState[key]) {
                    newEntryArray.push(entryArrayState[key]);
                }
            }
            if (newEntryArray?.length > 0) {
                setTableRowSectionState(prevState => { return { ...prevState, "entry": newEntryArray } });
                let defaultStatisticType = "";
                let defaultStatisticUnits = "";
                for (let itemIndex in newEntryArray) {
                    let evidenceReference = newEntryArray[itemIndex];
                    let evidenceId = getIdFromReference(evidenceReference);
                    if (!defaultStatisticType && combinedDictionary &&
                        combinedDictionary[evidenceId]) {
                        let evidenceJson = combinedDictionary[evidenceId];
                        if (evidenceJson.statistic?.[0]?.statisticType) {
                            defaultStatisticType = evidenceJson.statistic[0].statisticType;
                            if (!defaultStatisticUnits && evidenceJson.statistic?.[0]?.quantity?.unit) {
                                defaultStatisticUnits = evidenceJson.statistic[0].quantity.unit;
                            }
                        }
                    }
                }
                setStatisticDefaultsState({
                    "statisticType": defaultStatisticType, "units": defaultStatisticUnits
                });
            }
        }
    }, [entryArrayState]);

    useEffect(() => {
        if (referencedEvidenceRevisedState?.foi) {
            let newEntryArray = [];
            for (const key in entryArrayState) {
                if (entryArrayState[key]) {
                    newEntryArray.push(entryArrayState[key]);
                }
            }
            if (newEntryArray?.length > 0) {
                let defaultStatisticType = "";
                let defaultStatisticUnits = "";
                for (let itemIndex in newEntryArray) {
                    let evidenceReference = newEntryArray[itemIndex];
                    let evidenceId = getIdFromReference(evidenceReference);
                    if (evidenceId === referencedEvidenceRevisedState.foi) {
                        let evidenceJson = referencedEvidenceRevisedState.evidenceJson;
                        if (!defaultStatisticType && evidenceJson) {
                            if (evidenceJson.statistic?.[0]?.statisticType) {
                                defaultStatisticType = evidenceJson.statistic[0].statisticType;
                                if (!defaultStatisticUnits && evidenceJson.statistic?.[0]?.quantity?.unit) {
                                    defaultStatisticUnits = evidenceJson.statistic[0].quantity.unit;
                                }
                            }
                        }
                    } else if (!defaultStatisticType && combinedDictionary &&
                        combinedDictionary[evidenceId]) {
                        let evidenceJson = combinedDictionary[evidenceId];
                        if (evidenceJson.statistic?.[0]?.statisticType) {
                            defaultStatisticType = evidenceJson.statistic[0].statisticType;
                            if (!defaultStatisticUnits && evidenceJson.statistic?.[0]?.quantity?.unit) {
                                defaultStatisticUnits = evidenceJson.statistic[0].quantity.unit;
                            }
                        }
                    }
                }
                setStatisticDefaultsState({
                    "statisticType": defaultStatisticType, "units": defaultStatisticUnits
                });
            }
        }
    }, [referencedEvidenceRevisedState]);

    return <>
        <Table.Cell style={{ verticalAlign: "top" }}>
            {startingValue.title || startingValue.focus?.display}
            {editMode &&
                <span>
                    <p>
                        <span className={"unselectable"} style={{ cursor: "pointer" }}
                            onClick={() => {
                                setSelectedStatisticDefaultsState({
                                    "statisticType": statisticDefaultsState.statisticType || "",
                                    "units": statisticDefaultsState.units || ""
                                })
                            }}>
                            {(statisticDefaultsState.statisticType && !selectedStatisticDefaultsState.statisticType) &&
                                <>➕ Add Default Statistic Type {simpleDisplayStringFromFHIRCodeableConcept(statisticDefaultsState.statisticType)} and Units {statisticDefaultsState.units || "none"}</>}
                        </span>
                    </p>
                </span>
            }
        </Table.Cell>
        {entryArrayState && groupKeys.map((groupKey, groupIndex) => {
            let contained;
            if (groupKey === "group-assignment" && combinedDictionary[groupKey]) {
                contained = [combinedDictionary[groupKey]];
            }
            return <Table.Cell key={groupIndex} style={{ verticalAlign: "top" }}>
                <span>
                    <EvidenceTableCellDataEntry startingValue={entryArrayState[groupKey]}
                        outcomeName={outcomeName} outcomeFocus={startingValue.focus}
                        populationGroup={populationGroup}
                        synthesisPopulationGroup={synthesisPopulationGroup}
                        outcomeGroup={groupReferencesDictionary[groupKey]}
                        setEntryArrayState={setEntryArrayState}
                        statisticType={selectedStatisticDefaultsState.statisticType}
                        statisticUnit={selectedStatisticDefaultsState.units}
                        numberAnalyzed={sampleSizeDefaultState[groupKey]}
                        setReferencedEvidenceRevisedState={setReferencedEvidenceRevisedState}
                        contained={contained} />
                </span>
            </Table.Cell>
        })}
    </>
};

const TableRow = ({ startingValue, sectionPathString, foi, sampleSizeDefaultState,
    populationGroup, synthesisPopulationGroup
}) => {
    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));

    const [tableRowSectionState, setTableRowSectionState] = useState({});
    const [initialTableRowSectionState, setInitialTableRowSectionState] = useState({});

    useEffect(() => {
        const startingSection = calculateStartingSection(startingValue);
        setTableRowSectionState(startingSection);
        setInitialTableRowSectionState(startingSection);
    }, []);

    useEffect((() => {
        if (Object.keys(tableRowSectionState).length > 0 &&
            (JSON.stringify(initialTableRowSectionState) !== JSON.stringify(tableRowSectionState))) {
            let newSection = {};
            if (tableRowSectionState.extension) { newSection.extension = tableRowSectionState.extension; }
            if (tableRowSectionState.title) { newSection.title = tableRowSectionState.title; }
            if (tableRowSectionState.code) { newSection.code = tableRowSectionState.code; }
            if (Array.isArray(tableRowSectionState.author) && tableRowSectionState.author.length > 0) {
                newSection.author = tableRowSectionState.author;
            }
            if (tableRowSectionState.focus) { newSection.focus = tableRowSectionState.focus; }
            if (tableRowSectionState.text) { newSection.text = tableRowSectionState.text; }
            if (tableRowSectionState.orderedBy) { newSection.orderedBy = tableRowSectionState.orderedBy; }
            if (Array.isArray(tableRowSectionState.entry) && tableRowSectionState.entry.length > 0) {
                newSection.entry = tableRowSectionState.entry;
            }
            if (tableRowSectionState.emptyReason) { newSection.emptyReason = tableRowSectionState.emptyReason; }
            if (Array.isArray(tableRowSectionState.section) && tableRowSectionState.section.length > 0) {
                newSection.section = tableRowSectionState.section;
            }
            newSection = autoEditEmptyReason(newSection);
            if (Object.keys(newSection).length === 0 || (Object.keys(newSection).length === 1 && newSection.code)) {
                newSection = null;
            }
            newSection = JSON.parse(JSON.stringify(newSection));
            if (sectionPathString || sectionPathString === 0 || sectionPathString === "0") {
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                let sectionPath = sectionPathString.split(",");
                if (sectionPath.length === 1) {
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]] = newSection;
                } else if (sectionPath.length === 2) {
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]] = newSection;
                } else if (sectionPath.length === 3) {
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]] = newSection;
                } else if (sectionPath.length === 4) {
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]] = newSection;
                }
                console.log("resourceUpdate for Composition.section");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "Composition.section",
                    "section[" + sectionPathString + "].section",
                    initialTableRowSectionState,
                    newSection);
            }
        }
    }), [tableRowSectionState]);

    return <>
        {Object.keys(tableRowSectionState).length > 0 &&
            <TableRowSectionDataEntry startingValue={tableRowSectionState}
                setTableRowSectionState={setTableRowSectionState}
                sampleSizeDefaultState={sampleSizeDefaultState}
                populationGroup={populationGroup}
                synthesisPopulationGroup={synthesisPopulationGroup} />
        }
    </>
}

const EvidenceTable = ({ foi, reportType, populationGroup, synthesisPopulationGroup }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceCollection = useSelector(state => selectAllResources(state));
    const evidenceTableMap = useSelector(selectEvidenceTableMap);

    const [sampleSizeDefaultState, setSampleSizeDefaultState] = useState({});
    const [openAddTableRowModalState, setOpenAddTableRowModalState] = useState({ "openModal": false });

    useEffect(() => {
        let body = {
            foi: foi,
            parentResourceDetail: parentResourceDetail,
            resourceCollection: resourceCollection
        }
        dispatch(changeEvidenceTableMap(body));
    }, [resourceCollection]);

    return <div>
        <h3 id="evidence-table">Evidence Table</h3>
        {evidenceTableMap ?
            <>
                {openAddTableRowModalState.openModal &&
                    <AddTableRowModal resourceLoaded={evidenceTableMap}
                        setOpenAddTableRowModalState={setOpenAddTableRowModalState}
                        reportType={reportType} />}
                {(evidenceTableMap.groupReferencesDictionary &&
                    Object.keys(evidenceTableMap.groupReferencesDictionary).length > 0) ?
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Variable Name</Table.HeaderCell>
                                {Object.keys(evidenceTableMap.groupReferencesDictionary).map((groupKey) => {
                                    return <Table.HeaderCell key={groupKey}>
                                        {evidenceTableMap.groupReferencesDictionary[groupKey].display}
                                    </Table.HeaderCell>
                                })}
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {
                                (editMode && evidenceTableMap.outcomeGroupsQuantityDictionary &&
                                    Object.keys(evidenceTableMap.outcomeGroupsQuantityDictionary).length > 0
                                ) &&
                                <Table.Row key={"topline"}>
                                    <Table.Cell style={{ verticalAlign: "top" }}></Table.Cell>
                                    {Object.keys(evidenceTableMap.groupReferencesDictionary).map((groupKey, groupIndex) => {
                                        return <Table.Cell key={groupIndex} style={{ verticalAlign: "top" }}>
                                            {(evidenceTableMap.outcomeGroupsQuantityDictionary[groupKey] ||
                                                evidenceTableMap.outcomeGroupsQuantityDictionary[groupKey] === 0
                                            ) &&
                                                <p>
                                                    <span className={"unselectable"} style={{ cursor: "pointer" }}
                                                        onClick={() => {
                                                            setSampleSizeDefaultState(prevState => {
                                                                return {
                                                                    ...prevState,
                                                                    [groupKey]: evidenceTableMap.outcomeGroupsQuantityDictionary[groupKey],
                                                                    [groupIndex]: true
                                                                }
                                                            })
                                                        }}>
                                                        {
                                                            (!sampleSizeDefaultState[groupIndex]) &&
                                                            <>➕ Add {evidenceTableMap.outcomeGroupsQuantityDictionary[groupKey]} as Sample Size</>
                                                        }
                                                    </span>
                                                </p>}
                                        </Table.Cell>
                                    })}
                                </Table.Row>
                            }
                            {evidenceTableMap.resultsSectionsKeys?.length > 0 &&
                                evidenceTableMap.resultsSectionsKeys.map((sectionKey) => {
                                    let section = evidenceTableMap.resultsSectionsDictionary[sectionKey];
                                    return <Table.Row key={sectionKey}>
                                        <TableRow startingValue={section}
                                            sectionPathString={sectionKey}
                                            foi={foi} populationGroup={populationGroup}
                                            synthesisPopulationGroup={synthesisPopulationGroup}
                                            sampleSizeDefaultState={sampleSizeDefaultState} />
                                    </Table.Row>
                                })
                            }
                        </Table.Body>
                    </Table>
                    :
                    <><p>There are no results sections to produce an evidence table.</p></>
                }
                {editMode &&
                    <>
                        <Button style={{ color: "#FFFFFF", width: "230px", float: "left" }}
                            className="formButton"
                            content="+ Add Table Row" positive
                            onClick={() => {
                                setOpenAddTableRowModalState({ openModal: true });
                            }}
                        />
                        <br /><br />
                    </>
                }
            </>
            :
            <p>Loading...</p>
        }
    </div>
}

export default EvidenceTable;